import { ChevronDown, ChevronRight, Copy } from "lucide-react";
import { useState } from "react";

import { cn } from "@/lib/utils";

type Props = {
  nodeKey: string;
  value: unknown;
  depth: number;
  initialExpanded?: boolean;
  highlight?: string;
};

const TRUNCATE_LIMIT = 1000;

function typeOf(v: unknown): "object" | "array" | "string" | "number" | "boolean" | "null" {
  if (v === null) return "null";
  if (Array.isArray(v)) return "array";
  return typeof v as "object" | "string" | "number" | "boolean";
}

function tokenColor(type: string): string {
  switch (type) {
    case "string":
      return "text-[var(--color-coral-dark)]";
    case "number":
      return "text-[color:#0D6EFD]";
    case "boolean":
      return "text-[color:#6F42C1]";
    case "null":
      return "text-[var(--color-ink-soft)]";
    default:
      return "";
  }
}

export function JsonNode({ nodeKey, value, depth, initialExpanded = false, highlight }: Props) {
  const [expanded, setExpanded] = useState(initialExpanded);
  const [expandedLong, setExpandedLong] = useState(false);

  const type = typeOf(value);
  const indent = depth * 12;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(value, null, 2));
    } catch {
      /* ignore */
    }
  };

  // Scalar row
  if (type !== "object" && type !== "array") {
    let display = String(value);
    if (type === "string") display = `"${display}"`;
    const isLong = type === "string" && display.length > TRUNCATE_LIMIT + 2;
    const shown = isLong && !expandedLong ? `${display.slice(0, TRUNCATE_LIMIT)}...` : display;

    return (
      <div
        className="group flex items-center gap-2 rounded px-2 py-0.5 font-mono text-xs hover:bg-[var(--color-surface-muted)]"
        style={{ paddingLeft: indent + 24 }}
      >
        <span className="text-[var(--color-ink-muted)]">
          {nodeKey && `${highlightText(nodeKey, highlight)}:`}
        </span>
        <span className={cn("break-all", tokenColor(type))}>{highlightText(shown, highlight)}</span>
        {isLong && (
          <button
            type="button"
            onClick={() => setExpandedLong(!expandedLong)}
            className="text-[10px] text-[var(--color-ink-muted)] underline"
          >
            {expandedLong ? "省略" : `+${display.length - TRUNCATE_LIMIT} chars`}
          </button>
        )}
        <button
          type="button"
          onClick={handleCopy}
          aria-label="copy"
          className="ml-auto invisible group-hover:visible text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]"
        >
          <Copy size={12} />
        </button>
      </div>
    );
  }

  // Container
  const entries: Array<[string, unknown]> =
    type === "array"
      ? (value as unknown[]).map((v, i) => [String(i), v])
      : Object.entries(value as Record<string, unknown>);

  return (
    <div>
      <div
        className="group flex cursor-pointer items-center gap-2 rounded px-2 py-0.5 font-mono text-xs hover:bg-[var(--color-surface-muted)]"
        style={{ paddingLeft: indent + 4 }}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setExpanded(!expanded);
        }}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="text-[var(--color-ink-muted)]">
          {nodeKey && `${highlightText(nodeKey, highlight)}:`}
        </span>
        <span className="text-[var(--color-ink-soft)]">
          {type === "array" ? `[${entries.length}]` : `{${entries.length}}`}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleCopy();
          }}
          aria-label="copy"
          className="ml-auto invisible group-hover:visible text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]"
        >
          <Copy size={12} />
        </button>
      </div>
      {expanded && (
        <div>
          {entries.map(([k, v]) => (
            <JsonNode
              key={`${depth}-${k}`}
              nodeKey={k}
              value={v}
              depth={depth + 1}
              highlight={highlight}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function highlightText(text: string, _needle?: string): string {
  // Simple — no HTML for safety. Wrapped in parent <span>. Future: proper <mark> render.
  return text;
}
